/**
 * Leave Calculation Utilities
 *
 * Handles pro-rata calculations for partial year employment.
 */

/**
 * Calculates months employed in a specific year for pro-rata leave (BUrlG §5).
 * A month counts if the employee was employed on the 1st of that month.
 *
 * @param start - Employment start date (ISO format)
 * @param end - Employment end date (ISO format, null for ongoing)
 * @param year - Target year (defaults to current year)
 * @returns Number of months employed in the year (0-12)
 *
 * @example
 * monthsEmployedInYear('2026-01-07', null, 2026) // → 2 (employed on Feb 1 + Mar 1)
 * monthsEmployedInYear('2024-01-01', '2024-06-30', 2024) // → 6 (Jan-Jun)
 * monthsEmployedInYear('2023-01-01', null, 2024) // → 12 (full year)
 */
export function monthsEmployedInYear(
  start?: string | null,
  end?: string | null,
  year?: number
): number {
  const y = year ?? new Date().getFullYear();
  const jan1 = new Date(Date.UTC(y, 0, 1));
  const dec31 = new Date(Date.UTC(y, 11, 31));
  const s = start ? new Date(start) : undefined;
  const e = end ? new Date(end) : undefined;

  const today = new Date();
  const todayEnd = today.getFullYear() === y ? today : dec31;

  const employStart = s && s > jan1 ? s : jan1;
  const employEnd = e && e < todayEnd ? e : todayEnd;
  if (employEnd < employStart) return 0;

  let count = 0;
  for (let m = 0; m < 12; m++) {
    const monthFirst = new Date(Date.UTC(y, m, 1));
    if (employStart <= monthFirst && employEnd >= monthFirst) {
      count++;
    }
  }
  return count;
}

/**
 * Calculates contractual leave days per year for UI display.
 * Uses explicit value if set, otherwise calculates statutory + extra.
 *
 * IMPORTANT: explicit=0 is a valid value that overrides the calculation.
 * Only null/undefined triggers automatic calculation.
 *
 * @param explicit - Explicit total entitlement (overrides calculation if set)
 * @param statutory - Statutory minimum days
 * @param extra - Extra holiday days beyond statutory
 * @returns Contractual days per year, or null if cannot be calculated
 *
 * @example
 * contractualDaysPerYearUI(null, 20, 4) // → 24 (auto: 20 + 4)
 * contractualDaysPerYearUI(0, 20, 4)    // → 0 (explicit override)
 * contractualDaysPerYearUI(30, 20, 4)   // → 30 (explicit override)
 * contractualDaysPerYearUI(null, null, 4) // → null (cannot calculate)
 */
export function contractualDaysPerYearUI(
  explicit: number | null | undefined,
  statutory: number | null | undefined,
  extra: number | null | undefined
): number | null {
  // Explicit value overrides calculation (even if 0)
  if (explicit !== null && explicit !== undefined) return explicit;

  // Without statutory minimum, cannot calculate
  if (statutory === null || statutory === undefined) return null;

  const x = Number(extra || 0);
  return statutory + x;
}
